import { isNodeSelection, type Editor } from '@tiptap/react';
import { ClipboardCheck, GitCompareArrows, ListChecks, MessageSquareText, SearchCheck } from 'lucide-react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- Lib ---
import { isNodeInSchema } from '@/lib/tiptap-utils';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';

export type ActivityType = 'multipleChoice' | 'shortAnswer' | 'matching' | 'detectAnswer';

export interface ActivityOption {
    type: ActivityType;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    command: (editor: Editor) => void;
}

/**
 * Checks if an activity button should be disabled based on editor state and context.
 *
 * @param editor - The Tiptap editor instance
 * @param activityType - The type of activity to check
 * @returns true if the button should be disabled, false otherwise
 */
export function isActivityButtonDisabled(editor: Editor | null, activityType: ActivityType): boolean {
    if (!editor) {
        return true;
    }

    if (!editor.isEditable) {
        return true;
    }

    if (!isNodeInSchema(activityType, editor)) {
        return true;
    }

    const { state } = editor;
    const { selection, schema } = state;
    const { $from } = selection;

    // Get the node type for this activity
    const nodeType = schema.nodes[activityType];
    if (!nodeType) {
        return true;
    }

    // Check if parent allow insert of this node type
    const parentNodeDepth = $from.depth - 1 < 0 ? 0 : $from.depth - 1;
    const parentNode = $from.node($from.depth - 1);
    if (!parentNode) {
        return false;
    }

    if (parentNode.type.name === 'questionSet') {
        return true;
    }

    const canInsertBySchema = $from
        .node(parentNodeDepth)
        .canReplaceWith($from.index(parentNodeDepth), $from.index(parentNodeDepth), nodeType);

    return !canInsertBySchema;
}

export const activityOptions: ActivityOption[] = [
    {
        type: 'multipleChoice',
        label: 'Multiple Choice',
        icon: ListChecks,
        command: (editor: Editor) => {
            editor.commands.insertMultipleChoice();
        },
    },
    {
        type: 'shortAnswer',
        label: 'Short Answer',
        icon: MessageSquareText,
        command: (editor: Editor) => {
            editor.commands.insertShortAnswer();
        },
    },
    {
        type: 'matching',
        label: 'Matching',
        icon: GitCompareArrows,
        command: (editor: Editor) => {
            editor.commands.insertMatching();
        },
    },
    {
        type: 'detectAnswer',
        label: 'Detect Answer',
        icon: SearchCheck,
        command: (editor: Editor) => {
            editor.commands.insertDetectAnswer();
        },
    },
];

export interface QuestionSetMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor;
    /**
     * The activity types to display in the dropdown.
     */
    types?: ActivityType[];
    /**
     * Whether the dropdown should be hidden when no activity types are available
     * @default false
     */
    hideWhenUnavailable?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

export function canInsertActivity(editor: Editor | null, activityType: ActivityType): boolean {
    if (!editor) return false;

    // Check if the activity type is in the schema
    return isNodeInSchema(activityType, editor);
}

export function canInsertAnyActivity(editor: Editor | null, activityTypes: ActivityType[]): boolean {
    if (!editor) return false;
    return activityTypes.some(type => canInsertActivity(editor, type));
}

export function shouldShowActivityMenu(params: {
    editor: Editor | null;
    activityTypes: ActivityType[];
    hideWhenUnavailable: boolean;
    activityInSchema: boolean;
    canInsertAny: boolean;
}): boolean {
    const { editor, hideWhenUnavailable, activityInSchema, canInsertAny } = params;

    if (!activityInSchema || !editor) {
        return false;
    }

    if (hideWhenUnavailable) {
        if (isNodeSelection(editor.state.selection) || !canInsertAny) {
            return false;
        }
    }

    return true;
}

export function useActivityMenuState(editor: Editor | null, availableTypes: ActivityType[]) {
    const [isOpen, setIsOpen] = React.useState(false);

    const activityInSchema = availableTypes.some(type => isNodeInSchema(type, editor));

    const filteredActivities = React.useMemo(
        () => activityOptions.filter(option => availableTypes.includes(option.type)),
        [availableTypes],
    );

    const canInsertAny = canInsertAnyActivity(editor, availableTypes);

    const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
        setIsOpen(open);
        callback?.(open);
    }, []);

    return {
        isOpen,
        setIsOpen,
        activityInSchema,
        filteredActivities,
        canInsertAny,
        handleOpenChange,
    };
}

export function QuestionSetMenu({
    editor: providedEditor,
    types = ['multipleChoice', 'shortAnswer', 'matching', 'detectAnswer'],
    hideWhenUnavailable = false,
    onOpenChange,
    ...props
}: QuestionSetMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);

    const { isOpen, activityInSchema, filteredActivities, canInsertAny, handleOpenChange } = useActivityMenuState(editor, types);

    const show = React.useMemo(() => {
        return shouldShowActivityMenu({
            editor,
            activityTypes: types,
            hideWhenUnavailable,
            activityInSchema,
            canInsertAny,
        });
    }, [editor, types, hideWhenUnavailable, activityInSchema, canInsertAny]);

    const handleOnOpenChange = React.useCallback((open: boolean) => handleOpenChange(open, onOpenChange), [handleOpenChange, onOpenChange]);

    const handleActivityClick = React.useCallback(
        (activity: ActivityOption) => {
            if (!editor) return;
            activity.command(editor);
            handleOpenChange(false, onOpenChange);
        },
        [editor, handleOpenChange, onOpenChange],
    );

    if (!show || !editor || !editor.isEditable) {
        return null;
    }

    return (
        <DropdownMenu
            open={isOpen}
            onOpenChange={handleOnOpenChange}
        >
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    data-style="ghost"
                    data-active-state="off"
                    role="button"
                    tabIndex={-1}
                    aria-label="Insert question set"
                    tooltip="Question Set"
                    id="question-set-menu-trigger"
                    {...props}
                >
                    <ClipboardCheck className="tiptap-button-icon" />
                    <ChevronDownIcon className="tiptap-button-dropdown-small" />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
                <Card>
                    <CardBody>
                        <DropdownMenuGroup>
                            {filteredActivities.map(activity => {
                                const isDisabled = isActivityButtonDisabled(editor, activity.type);

                                return isNodeInSchema(activity.type, editor) ? (
                                    <DropdownMenuItem
                                        key={activity.type}
                                        asChild
                                        disabled={isDisabled}
                                    >
                                        <Button
                                            type="button"
                                            data-style="ghost"
                                            role="button"
                                            tabIndex={-1}
                                            disabled={isDisabled}
                                            onClick={() => handleActivityClick(activity)}
                                        >
                                            <div className="flex-start flex w-full">
                                                <activity.icon className="mr-2 h-4 w-4" />
                                                <span>{activity.label}</span>
                                            </div>
                                        </Button>
                                    </DropdownMenuItem>
                                ) : null;
                            })}
                        </DropdownMenuGroup>
                    </CardBody>
                </Card>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default QuestionSetMenu;
